import { useQuery, useQueryClient } from "@tanstack/react-query";
import Image from "next/image";
import Link from "next/link";

import LocalePs from "../locale/localePs.core";
import { Tag } from "../reuseables/list.comp";
import { useTranslation } from "../../core/translation.core";
import { Category } from "../../types/category";
import { post } from "../../core/fetch.core";
import ArrowRightIcon from "../../icons/arrowRight.icon";
import ArrowLeftIcon from "../../icons/arrowLeft.icon";
import Toggle from "../reuseables/toggle.core";
import PencilIcon from "../../icons/pencil.icon";
import { fancyCheckboxCss } from "../reuseables/form.comp";
import { UseFormReturn, useWatch } from "react-hook-form";
import toggleRowSelect from "../reuseables/toggleRowSelect";
import { SelectFormData } from "../../types/form";
import { editPosition, getCategories } from "../../core/category.core";
import { useState } from "react";

function useSyncCheck(methods: UseFormReturn<SelectFormData, any>) {
  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });

  function syncChecked(categoryId: string) {
    const isChecked = methods.getValues("selectIds").includes(categoryId);

    if (isChecked) {
      const selectIds = methods.getValues("selectIds");
      if (checkAllChildren(selectIds, categoryId)) {
        methods.setValue("selectIds", selectIds);
      }
    } else {
      const selectIds = methods.getValues("selectIds");
      if (uncheckAllParents(selectIds, categoryId)) {
        methods.setValue("selectIds", selectIds);
      }
      if (uncheckAllChildren(selectIds, categoryId)) {
        methods.setValue("selectIds", selectIds);
      }
    }
  }

  function checkAllChildren(selectIds: string[], categoryId: string) {
    const childIds = categories!.filter((c) => c.parentId === categoryId);

    let hasChange = false;
    for (const childId of childIds) {
      if (!selectIds.includes(childId.id)) {
        selectIds.push(childId.id);
        hasChange = true;
      }

      if (checkAllChildren(selectIds, childId.id)) {
        hasChange = true;
      }
    }

    return hasChange;
  }

  function uncheckAllChildren(selectIds: string[], categoryId: string) {
    const childIds = categories!.filter((c) => c.parentId === categoryId);

    let hasChange = false;
    for (const childId of childIds) {
      const index = selectIds.indexOf(childId.id);
      if (index !== -1) {
        selectIds.splice(index, 1);
        hasChange = true;
      }

      if (uncheckAllChildren(selectIds, childId.id)) {
        hasChange = true;
      }
    }

    return hasChange;
  }

  function uncheckAllParents(selectIds: string[], categoryId: string) {
    const parentId = categories!.find((c) => c.id === categoryId)?.parentId;

    if (!parentId) return false;

    const parent = categories!.find((c) => c.id === parentId);
    if (!parent) return false;

    let hasChange = false;

    const index = selectIds.indexOf(parent.id);
    if (index !== -1) {
      selectIds.splice(index, 1);
      hasChange = true;
    }

    if (uncheckAllParents(selectIds, parent.id)) {
      hasChange = true;
    }

    return hasChange;
  }

  return syncChecked;
}

export default function CategoryRow({
  category,
  siblings,
  indent = 0,
  methods,
}: {
  category: Category;
  siblings: Category[];
  indent: number;
  methods: UseFormReturn<SelectFormData, any>;
}) {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();
  const selectIds = useWatch({ name: "selectIds", control: methods.control });
  const syncChecked = useSyncCheck(methods);
  const [enableRowSelect, setEnableRowSelect] = useState(false);

  const enableSelect = selectIds && selectIds.length > 0;

  function getPrevious() {
    const idx = siblings.findIndex((item) => item.id === category.id);
    return siblings[idx - 1].id;
  }

  async function togglePromotion(isPromotion: boolean) {
    await post("/admin/category/setIsPromotion", {
      id: category.id,
      isPromotion,
    });

    queryClient.invalidateQueries({ queryKey: ["categories"] });
  }

  async function enableCategory(enabled: boolean) {
    await post("/admin/category/setEnable", {
      id: category.id,
      enabled,
    });

    queryClient.invalidateQueries({ queryKey: ["categories"] });
  }

  function CheckboxCell() {
    if (enableSelect || enableRowSelect) {
      return (
        <input
          className={fancyCheckboxCss}
          type="checkbox"
          value={category.id}
          checked={methods.getValues("selectIds").includes(category.id)}
          onChange={() => {
            toggleRowSelect(methods, "selectIds", category.id);
            syncChecked(category.id);
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      );
    }
    return null;
  }

  return (
    <div
      id={category.id}
      className={`category-row grid grid-cols-[1fr_1fr_15px] lg:space-y-0 lg:grid-cols-[15px_4fr_4fr_4fr_60px_60px_60px_110px_110px_60px] gap-x-6 gap-y-3 lg:gap-y-3 px-2 lg:px-0 lg:pr-6 py-2 border-[1px] text-sm ${
        indent ? `ml-[${"10"}rem] ` : ""
      } ${enableSelect ? "cursor-pointer" : ""} `}
      style={{ marginLeft: indent * 20 }}
      draggable={true}
      onDragStart={(ev) => {
        ev.dataTransfer.setData("text/plain", category.id);
      }}
      onClick={(e) => {
        e.stopPropagation();

        toggleRowSelect(methods, "selectIds", category.id);
        syncChecked(category.id);
      }}
      onMouseEnter={() => {
        setEnableRowSelect(true);
      }}
      onMouseLeave={() => {
        setEnableRowSelect(false);
      }}
    >
      <div className="flex col-start-3 col-end-3 lg:col-start-auto lg:col-end-auto">
        <div className="hidden md:block bg-gray-100 dark:bg-gray-400 mt-[-0.5rem] h-[calc(100%+1rem)] min-w-2 hover:bg-gray-400 dark:hover:bg-gray-900"></div>
        <div className="lg:pl-2 flex items-center min-w-[31.2px]">
          <CheckboxCell />
        </div>
      </div>
      <div className="flex items-center row-start-1 lg:row-start-auto col-span-2 lg:col-span-1">
        {category.parentId && enableRowSelect && (
          <button
            className="h-[calc(100%+1rem)] w-7 hover:bg-gray-400 dark:hover:bg-gray-900 mx-1"
            onClick={() => {
              editPosition(category.id, category.parent!.parentId, category.parentId, queryClient);
            }}
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
        )}
        {siblings[0].id !== category.id && enableRowSelect && (
          <button
            className="h-[calc(100%+1rem)] w-7 hover:bg-gray-400 dark:hover:bg-gray-900 mr-1"
            onClick={() => {
              editPosition(category.id, getPrevious(), undefined, queryClient);
            }}
          >
            <ArrowRightIcon />
          </button>
        )}
        <Tag>{t(msg.category.code)}</Tag>&nbsp;{category.code} ({category.productCount})
      </div>
      <div className="grid grid-cols-[130px_1fr] lg:grid-cols-[1fr] col-span-3 lg:col-span-1">
        <Tag>{t(msg.category.name)}</Tag>
        <div className="self-center">
          <LocalePs value={category.name} />
        </div>
      </div>
      <div className="grid grid-cols-[130px_1fr] lg:grid-cols-[1fr] col-span-3 lg:col-span-1">
        <Tag>{t(msg.category.slug)}</Tag>
        <div className="self-center">
          <LocalePs value={category.slug} />
        </div>
      </div>
      <div className="col-span-3 lg:col-span-1">
        <Tag>{t(msg.category.rewardPayout)}</Tag>&nbsp;{category.rewardPayout}
      </div>
      <div className="col-span-3 lg:col-span-1 self-center">
        <Tag>{t(msg.category.isPromotion)}</Tag>&nbsp;
        <Toggle
          title=""
          isChecked={category.isPromotion ?? false}
          onChange={(isChecked: boolean) => {
            togglePromotion(isChecked);
          }}
        />
      </div>
      <div className="flex items-center">
        <Toggle
          title=""
          isChecked={category.enabled}
          onChange={(isChecked: boolean) => {
            enableCategory(isChecked);
          }}
        />
      </div>
      <div className="col-span-3 lg:col-span-1 flex justify-center items-center">
        {category.featuredMedia && (
          <Image
            src={category.featuredMedia.thumbnailUrl}
            alt="Featured Media"
            width={100}
            height={Math.round((category.featuredMedia.thumbnailHeight / category.featuredMedia.thumbnailWidth) * 100)}
            className="object-cover rounded"
          />
        )}
      </div>
      <div className="col-span-3 lg:col-span-1 flex justify-center items-center">
        {category.iconMedia && (
          <Image
            src={category.iconMedia.thumbnailUrl}
            alt="Icon Media"
            width={100}
            height={Math.round((category.iconMedia.thumbnailHeight / category.iconMedia.thumbnailWidth) * 100)}
            className="object-cover rounded"
          />
        )}
      </div>
      <div className="self-center">
        <Link className="catgory-edit cursor-pointer" href={`category/edit/?id=${category.id}`}>
          <PencilIcon className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
